// src/pages/Admin/ManageGallery/AddImage.jsx
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAdmin } from "../../../context/AdminContext";
import { useNavigate } from "react-router-dom";

const CATEGORIES = [
  "Hotels & Restaurants",
  "Tea Factories & Industries",
  "Home & Living",
  "Others",
];

const MAX_IMAGES = 10;
const MAX_SIZE = 2 * 1024 * 1024;

const isBlobUrl = (url) => typeof url === "string" && url.startsWith("blob:");

const AddImage = () => {
  const { admin, token } = useAdmin();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // cleanup previews
  useEffect(() => {
    return () => {
      previews.forEach((p) => {
        if (isBlobUrl(p)) URL.revokeObjectURL(p);
      });
    };
  }, [previews]);

  const validateFile = (file) => {
    if (!file) return "Invalid file";

    if (!file.type.startsWith("image/")) {
      return "Only image files allowed";
    }

    if (file.size > MAX_SIZE) {
      return "Each image must be under 2MB";
    }

    return null;
  };

  const processFiles = (files) => {
    const fileArray = Array.from(files);

    if (images.length + fileArray.length > MAX_IMAGES) {
      setError(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }

    const newImages = [];
    const newPreviews = [];

    for (const file of fileArray) {
      const err = validateFile(file);
      if (err) {
        setError(err);
        return;
      }

      newImages.push(file);
      newPreviews.push(URL.createObjectURL(file));
    }

    setImages((prev) => [...prev, ...newImages]);
    setPreviews((prev) => [...prev, ...newPreviews]);
    setError("");
  };

  const handleFileChange = (e) => {
    processFiles(e.target.files);
    e.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    processFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => e.preventDefault();

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setPreviews((prev) => {
      const updated = [...prev];
      if (isBlobUrl(updated[index])) URL.revokeObjectURL(updated[index]);
      updated.splice(index, 1);
      return updated;
    });
  };

  const resetForm = () => {
    previews.forEach((p) => {
      if (isBlobUrl(p)) URL.revokeObjectURL(p);
    });

    setTitle("");
    setCategory("");
    setImages([]);
    setPreviews([]);
    setError("");

    if (fileRef.current) fileRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!category) return setError("Category is required");
    if (images.length === 0) return setError("Select at least one image");

    try {
      setLoading(true);
      setError("");

      const authToken =
        token || admin?.token || localStorage.getItem("adminToken");

      const formData = new FormData();
      formData.append("title", title.trim());
      formData.append("category", category);

      images.forEach((img) => {
        formData.append("image", img);
      });

  const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

      await axios.post(`${API}/api/gallery`, formData, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "multipart/form-data",
        },
      });

      alert("Images uploaded successfully");
      resetForm();
      navigate("/admin/manage-gallery");
    } catch (err) {
      setError(err.response?.data?.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-800 text-white p-6">
      {/* BACK */}
      <button
        onClick={() => navigate("/admin/manage-gallery")}
        className="mb-6 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg border border-white/10"
      >
        ← Back
      </button>

      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto space-y-6">
        {/* HEADER */}
        <div className="bg-white/10 p-5 rounded-2xl border border-white/10">
          <h1 className="text-2xl font-semibold">Add Gallery Images</h1>
          <p className="text-sm text-gray-400">
            Upload multiple images at once (max {MAX_IMAGES})
          </p>
        </div>

        {/* ERROR */}
        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-300 px-4 py-2 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* FORM */}
        <div className="bg-white/10 p-6 rounded-2xl border border-white/10 space-y-4">
          {/* TITLE */}
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title (optional)"
            className="w-full p-3 rounded-lg bg-white/5 border border-white/10 focus:border-green-500 outline-none"
          />

          {/* CATEGORY */}
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full p-3 rounded-lg bg-gray-900 border border-white/10 focus:border-green-500 outline-none"
          >
            <option value="">Select category</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {/* DROP AREA */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-white/20 p-10 rounded-2xl text-center text-gray-300 hover:border-green-500 transition"
        >
          Drag & drop images here or click below
          <input
            ref={fileRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileChange}
            className="block mx-auto mt-4"
          />
          <p className="text-xs text-gray-500 mt-2">
            {images.length}/{MAX_IMAGES} selected
          </p>
        </div>

        {/* PREVIEW GRID */}
        {previews.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {previews.map((src, index) => (
              <div key={index} className="relative">
                <img
                  src={src}
                  alt={`preview-${index}`}
                  className="w-full h-28 object-cover rounded-lg border border-white/10"
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-1 right-1 bg-black/70 w-6 h-6 rounded-full"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* SUBMIT */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-60 py-3 rounded-xl font-semibold"
        >
          {loading ? "Uploading..." : "Upload Images"}
        </button>
      </form>
    </div>
  );
};

export default AddImage;
